import { getStudioAuthProvider } from "@/services/cogo-platform-config";
import { isApplianceDistribution } from "@/services/studio-presentation-profile";
import { applianceHidesPublicPath } from "@/services/appliance-commerce-surface";

type BrowserRouteAuthority = Pick<Window, "history" | "location">;

/**
 * Appliance entry canonicalization (operator, 2026-08-10): on an appliance the
 * first screen is the sign-in screen, not the cloud marketing landing. The
 * marketing pages advertise a cloud product the closed-network install is not;
 * `/studio` is the appliance's real front door — unauthenticated it renders
 * the id/password AuthPage (App.tsx auth gate), authenticated it renders Home.
 *
 * Cloud/dev builds (auth provider !== 'cogo') keep `/` → marketing landing.
 */
export function isApplianceEntryRoute(route: string): boolean {
  return route === "/" || route === "/homepage";
}

/**
 * True when this build must send `route` to `/studio` instead of rendering
 * the cloud public shell at it.
 *
 * TWO signals, deliberately OR-ed (2026-08-18 appliance-first enforcement):
 *
 *   - `VITE_STUDIO_DISTRIBUTION=appliance` — the deployment's declared
 *     IDENTITY, and the single predicate every other appliance surface
 *     already gates on (route registry, template catalogue, featured
 *     gallery, chat template proposal).
 *   - auth provider `cogo` — the signal this gate originally read, and the
 *     only one it read until now.
 *
 * They were the same value in practice because `.env.appliance` sets both,
 * which is precisely why reading only one could not be caught: a build that
 * declares itself an appliance while carrying a different `VITE_AUTH_PROVIDER`
 * silently got the cloud marketing landing as its first screen, and no
 * assertion anywhere would have gone red. "Login is the first screen" is a
 * property of the DISTRIBUTION, so the distribution predicate must be able to
 * decide it on its own.
 *
 * Also covers the public commerce pages (`/pricing`, `/checkout/*`,
 * `/refund`) — see `applianceHidesPublicPath`. Those are appliance-only
 * (never redirected on a cloud build with `cogo` auth), because they are a
 * commerce-removal concern rather than an entry-screen one.
 */
export function shouldRewriteToStudioEntry(route: string): boolean {
  if (applianceHidesPublicPath(route)) return true;
  if (!isApplianceEntryRoute(route)) return false;
  return isApplianceDistribution() || getStudioAuthProvider() === "cogo";
}

export function replaceApplianceEntryRoute(
  browser: BrowserRouteAuthority,
): string {
  const destination = `/studio${browser.location.search}${browser.location.hash}`;
  browser.history.replaceState(null, "", destination);
  return destination;
}
